import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, FileText, Radio } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { api, errMsg, useApi, ago } from "../lib/api";
import { PulseGauge } from "../components/public/SentimentPanel";
import { Alerts, Heatmap, Funnel } from "../components/exec/Panels";
import { EntityMap, Competitive } from "../components/exec/EntityMap";
import { Decision, TwiFlag } from "../components/Badges";

const Stat = ({ k, v, tone = "text-ink", testId }) => (
  <div className="panel p-4" data-testid={testId}><div className={`font-data text-3xl ${tone}`}>{v}</div><div className="font-data text-[10px] uppercase tracking-wider text-slate-500 mt-1">{k}</div></div>
);

const PeoplePanel = ({ s }) => (
  <div className="panel p-5" data-testid="people-panel">
    <div className="eyebrow mb-4">People right now</div>
    <div className="space-y-3 text-sm">
      <div><span className="text-red-400 inline-flex items-center gap-1"><Radio size={12} /> On air:</span> <span className="text-slate-200">{s?.on_air_now?.join(", ") || "No verified host on air this hour"}</span></div>
      <div><span className="text-green-400">Available for urgent assignment:</span> <span className="text-slate-300">{s?.available?.slice(0, 6).join(", ") || "—"}</span></div>
      <div><span className="text-amber-400">Overloaded:</span> <span className="text-slate-300">{s?.overloaded?.map((o) => `${o.name} (${o.open_tasks})`).join(", ") || "None"}</span></div>
      <div><span className="text-slate-400">Underutilised:</span> <span className="text-slate-300">{s?.underutilised?.join(", ") || "None"}</span></div>
      <div><span className="text-fuchsia-300">Inactive 30+ days:</span> <span className="text-slate-300">{s?.inactive_30d?.join(", ") || "None"}</span></div>
    </div>
    <p className="text-[11px] text-slate-500 mt-3">{s?.sample_records} of {s?.total} staff records are labelled sample until HR import.</p>
    <Decision>Who needs support, who can take the next big story.</Decision>
  </div>
);

export default function Executive() {
  const { data: ov, reload } = useApi("/exec/overview");
  const [busy, setBusy] = useState("");
  const nav = useNavigate();
  const refresh = async () => {
    setBusy("scrape");
    try {
      const r = await api.post("/exec/scrape");
      toast.success(`Intelligence refreshed: ${r.data.scraped ?? 0} articles, ${r.data.analysed ?? 0} newly analysed`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const report = async () => {
    setBusy("report");
    try {
      await api.post("/exec/report");
      nav("/app/report");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  const s = ov?.sentiment;
  return (
    <div data-testid="executive-dashboard">
      <PageHead eyebrow="Portal D · For Dr. Osei Kwame Despite & Dr. Ernest Ofori Sarpong" title="Executive Intelligence"
        actions={<>
          <button onClick={refresh} disabled={!!busy} className="btn-ghost" data-testid="exec-refresh-btn"><RefreshCw size={14} className={busy === "scrape" ? "animate-spin" : ""} /> {busy === "scrape" ? "Scraping…" : "Refresh intelligence"}</button>
          <button onClick={report} disabled={!!busy} className="btn-gold" data-testid="executive-pdf-generate-btn"><FileText size={14} /> {busy === "report" ? "Claude is writing…" : "Give me today's summary"}</button>
        </>}>
        Last scrape {ago(ov?.scrape?.last_run)} · {ov?.scrape?.count ?? 0} Peacefmonline articles tracked.
      </PageHead>
      {!ov ? <div className="p-10 font-data text-xs text-slate-500">Assembling intelligence…</div> : (
        <div className="p-5 md:p-10 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat k="Staff tracked" v={ov.staff.total} testId="stat-staff" />
            <Stat k="Open tasks" v={ov.operations.open_tasks} testId="stat-tasks" />
            <Stat k="Stories in flight" v={ov.operations.stories_in_flight} testId="stat-stories" />
            <Stat k="Pending leave" v={ov.operations.pending_leave} tone="text-amber-300" testId="stat-leave" />
            <Stat k="New listener feedback" v={ov.operations.new_feedback} tone="text-gold" testId="stat-feedback" />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="panel p-5" data-testid="exec-sentiment-panel">
              <div className="eyebrow mb-4">Real-time sentiment pulse</div>
              <PulseGauge s={s} testId="exec-sentiment-pulse" />
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="text-slate-400">Kokrokoo mentions: <span className="text-ink font-data">{s.kokrokoo_mentions}</span></div>
                <div className="text-slate-400">Political share: <span className="text-ink font-data">{Math.round(s.political_share * 100)}%</span></div>
              </div>
              {s.twi_items_needing_validation > 0 && <div className="mt-2"><TwiFlag /> <span className="text-xs text-slate-400">{s.twi_items_needing_validation} items</span></div>}
              <div className="mt-3 space-y-1">{Object.entries(s.by_entity).map(([e, b]) => <div key={e} className="text-[11px] text-slate-400 flex justify-between"><span>{e}</span><span className="font-data">+{b.positive} / {b.neutral} / −{b.negative}</span></div>)}</div>
              <Decision>Whether Ghana's mood around DGC platforms needs a direct response today.</Decision>
            </div>
            <div className="lg:col-span-2"><Alerts alerts={ov.alerts} /></div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6"><Heatmap data={ov.heatmap} /><PeoplePanel s={ov.staff} /></div>
          <div className="grid lg:grid-cols-2 gap-6"><Funnel r={ov.recruitment} /><Competitive rows={ov.competitive} /></div>
          <EntityMap entities={ov.assets} />
        </div>
      )}
    </div>
  );
}
